import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarcodeFormat, BrowserMultiFormatReader } from '@zxing/browser';
import type { IScannerControls } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';
import { useAuth, useEvent } from '../../context';
import { getVendorInfo } from '../../api/tipping';
import { getVoteStatus, submitVote } from '../../api/vote';
import type { VoteVendor } from '../../api/vote';
import { Button, ActivityIndicator } from '../../components/ui';
import { images } from '../../config/colors';
import tocBackgroundFallback from '../../assets/tocBackground.jpg';
import styles from './VotePage.module.css';
import type { EventData } from '../../api/event';

const tocBackground = images.background || tocBackgroundFallback;

type PageState = 'loading' | 'vote' | 'confirm-scan' | 'results';

interface LocationState {
  event?: EventData;
}

export function VotePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginKey } = useAuth();
  const { selectedEvent: contextEvent } = useEvent();
  const { event: stateEvent } = (location.state as LocationState) || {};
  const selectedEvent = stateEvent || contextEvent;

  const [pageState, setPageState] = useState<PageState>('loading');
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<VoteVendor[]>([]);
  const [votedVendorId, setVotedVendorId] = useState<number | null>(null);
  const [pendingVendor, setPendingVendor] = useState<VoteVendor | null>(null);
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  const cleanupCamera = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    streamRef.current?.getTracks().forEach((track) => {
      if (track.readyState === 'live') {
        track.stop();
        track.enabled = false;
      }
    });
    streamRef.current = null;
  }, []);

  const loadVoteStatus = useCallback(async () => {
    if (!loginKey || !selectedEvent) return;

    setLoading(true);
    const response = await getVoteStatus(loginKey, selectedEvent.id);
    setLoading(false);

    if (!response.ok || response.data?.error) {
      alert(response.data?.error?.message || "Couldn't load vote status. Please try again.");
      return;
    }

    const data = response.data!;
    const sortedVendors = [...data.vendors].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    setVendors(sortedVendors);

    if (data.has_voted && data.voted_vendor_id) {
      setVotedVendorId(data.voted_vendor_id);
      setPageState('results');
    } else {
      setPageState('vote');
    }
  }, [loginKey, selectedEvent]);

  useEffect(() => {
    loadVoteStatus();
  }, [loadVoteStatus]);

  const handleScan = useCallback(async (qrCode: string) => {
    if (!loginKey || !selectedEvent || loading) return;

    setLoading(true);
    cleanupCamera();
    setScanning(false);

    const response = await getVendorInfo(loginKey, selectedEvent.id, qrCode);
    setLoading(false);

    if (response.ok && response.data?.vendor) {
      const scannedVendor = vendors.find((v) => v.id === response.data!.vendor!.id);
      if (scannedVendor) {
        setPendingVendor(scannedVendor);
        setPageState('confirm-scan');
      } else {
        alert('This vendor is not participating in voting for this event.');
      }
    } else {
      alert(response.data?.error?.message || 'Vendor not found. Please try again.');
    }
  }, [loginKey, selectedEvent, loading, cleanupCamera, vendors]);

  useEffect(() => {
    if (!scanning) return;

    let cancelled = false;

    const startScanning = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { max: 1920 },
            height: { max: 1920 },
            facingMode: { ideal: 'environment' },
          },
        });

        if (cancelled) {
          mediaStream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = mediaStream;
        const videoTracks = mediaStream.getVideoTracks();
        const track = videoTracks[0];
        const deviceId = track.getSettings().deviceId;

        if (deviceId && videoRef.current) {
          const hints = new Map();
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);

          const reader = new BrowserMultiFormatReader();
          reader.setHints(hints);

          const controls = await reader.decodeFromVideoDevice(
            deviceId,
            videoRef.current,
            (result, error) => {
              if (!error && result && !cancelled) {
                handleScan(result.getText());
              }
            },
          );

          if (cancelled) {
            controls.stop();
          } else {
            controlsRef.current = controls;
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const error = err as Error;
          if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
            setCameraError('Camera access denied. Please allow camera permissions and try again.');
          } else {
            setCameraError('Could not access camera. Please check your device settings.');
          }
        }
      }
    };

    startScanning();

    return () => {
      cancelled = true;
      cleanupCamera();
    };
  }, [scanning, cleanupCamera, handleScan]);

  const handleVendorSelect = (vendor: VoteVendor) => {
    setPendingVendor(vendor);
    setPageState('confirm-scan');
  };

  const handleConfirmVote = async () => {
    if (!loginKey || !selectedEvent || !pendingVendor) return;

    setLoading(true);
    const response = await submitVote(loginKey, selectedEvent.id, pendingVendor.id);
    setLoading(false);

    if (response.ok && response.data?.success) {
      setVotedVendorId(pendingVendor.id);
      setPageState('results');
    } else {
      alert(response.data?.error?.message || 'Could not submit vote. Please try again.');
    }
  };

  const handleCancelConfirm = () => {
    setPendingVendor(null);
    setPageState('vote');
  };

  const handleChangeVote = () => {
    setPendingVendor(null);
    setPageState('vote');
  };

  const handleStartScan = () => {
    setCameraError('');
    setScanning(true);
  };

  const handleCancelScan = () => {
    cleanupCamera();
    setScanning(false);
  };

  const votedVendorName = vendors.find((v) => v.id === votedVendorId)?.name || '';

  if (!selectedEvent) {
    return (
      <div className={styles.background} style={{ backgroundImage: `url(${tocBackground})` }}>
        <div className={styles.container}>
          <p className={styles.title}>Best in Show</p>
          <p className={styles.subtitle}>Please select an event first.</p>
          <Button theme="outline" onClick={() => navigate('/event')}>Go to Event Info</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.background} style={{ backgroundImage: `url(${tocBackground})` }}>
      <ActivityIndicator visible={loading} />
      <div className={styles.container}>
        {pageState === 'loading' && (
          <p className={styles.subtitle}>Loading...</p>
        )}

        {pageState === 'vote' && (
          <>
            <p className={styles.title}>People's Choice</p>
            <p className={styles.subtitle}>Vote for your favorite vendor!</p>

            {!scanning && (
              <Button onClick={handleStartScan}>Scan Vendor QR</Button>
            )}

            {scanning && (
              <>
                <div className={styles.videoContainer}>
                  <video ref={videoRef} autoPlay className={styles.video} />
                </div>
                {cameraError && <p className={styles.cameraError}>{cameraError}</p>}
                <Button theme="outline" onClick={handleCancelScan}>Cancel Scan</Button>
              </>
            )}

            {!scanning && vendors.length > 0 && (
              <>
                <p className={styles.orDivider}>or select a vendor</p>
                <div className={styles.vendorList}>
                  {vendors.map((vendor) => (
                    <button
                      key={vendor.id}
                      className={styles.vendorCard}
                      onClick={() => handleVendorSelect(vendor)}
                    >
                      {vendor.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            <Button theme="outline" onClick={() => navigate(-1)}>Back</Button>
          </>
        )}

        {pageState === 'confirm-scan' && pendingVendor && (
          <>
            <p className={styles.title}>Confirm Vote</p>
            <p className={styles.subtitle}>Vote for {pendingVendor.name}?</p>
            <div className={styles.buttonRow}>
              <Button onClick={handleConfirmVote}>Confirm</Button>
              <Button theme="outline" onClick={handleCancelConfirm}>Cancel</Button>
            </div>
          </>
        )}

        {pageState === 'results' && (
          <>
            <p className={styles.title}>Best in Show</p>
            <p className={styles.subtitle}>Thank you for voting!</p>
            {votedVendorName && (
              <p className={styles.yourVote}>Your vote: {votedVendorName}</p>
            )}
            <Button theme="outline" onClick={handleChangeVote}>Change Vote</Button>
            <Button theme="outline" onClick={() => navigate('/', { replace: true })}>Done</Button>
          </>
        )}
      </div>
    </div>
  );
}

export default VotePage;
